import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import { usePageMeta } from "@/hooks/usePageMeta";

const sections = [
  { id: "mission", label: "网站定位" },
  { id: "scope", label: "内容范围" },
  { id: "contact", label: "联系方式" },
  { id: "disclaimer", label: "免责声明" },
];

const scopeItems = [
  { icon: "ri-newspaper-line", title: "动漫资讯", desc: "最新动漫动态、活动预告、行业访谈与作品评测。" },
  { icon: "ri-movie-2-line", title: "作品推荐", desc: "按类型、地区、年代整理的动漫作品资料与观看指南。" },
  { icon: "ri-stack-line", title: "专题合集", desc: "围绕特定主题策划的片单与内容合集。" },
];

export default function About() {
  usePageMeta(
    "关于我们 - 91动漫",
    "了解 91动漫的网站定位、内容范围与联系方式。我们致力于为动漫爱好者提供及时、清晰的资讯与推荐服务。",
    "91动漫,关于我们,动漫资讯站,联系方式"
  );

  return (
    <div className="w-full">
      <PageHero
        title="关于我们"
        description="91动漫致力于为动漫爱好者提供及时、清晰的动漫资讯与作品推荐服务，帮助你轻松了解热门动漫。"
        crumbs={[{ label: "关于我们" }]}
        icon="ri-information-line"
      />

      <section className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[220px_1fr] lg:gap-12">
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <nav className="flex flex-row flex-wrap gap-2 rounded-lg border border-background-200 bg-background-50 p-3 lg:flex-col">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                >
                  {s.label}
                </a>
              ))}
            </nav>
          </aside>

          <div className="flex flex-col gap-10">
            <section id="mission" className="scroll-mt-24">
              <h2 className="font-heading text-2xl font-extrabold text-foreground-950">网站定位</h2>
              <p className="mt-3 text-[15px] leading-[1.9] text-foreground-700">
                91动漫是一个面向广大动漫爱好者的资讯门户。我们以“让了解动漫变得更简单”为目标，
                聚合动漫新闻动态、整理作品资料、策划主题专题，帮助用户在信息繁多的动漫世界中，
                快速掌握最新动态，发现真正值得一看的作品。
              </p>
              <p className="mt-3 text-[15px] leading-[1.9] text-foreground-700">
                无论你是想追踪新番进度，还是想系统回顾经典，或是寻找下一部想看的好作品，
                我们都希望 91动漫 能成为你可靠的参考伙伴。
              </p>
            </section>

            <section id="scope" className="scroll-mt-24">
              <h2 className="font-heading text-2xl font-extrabold text-foreground-950">内容范围</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {scopeItems.map((s) => (
                  <div key={s.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                      <i className={`${s.icon} text-xl`} />
                    </span>
                    <h3 className="mt-3 font-heading text-base font-bold text-foreground-900">{s.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-500">{s.desc}</p>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm leading-relaxed text-foreground-500">
                想快速浏览？可以前往
                <Link to="/news" className="mx-1 font-medium text-accent-600 hover:text-accent-700">
                  动漫资讯
                </Link>
                或
                <Link to="/recommend" className="mx-1 font-medium text-accent-600 hover:text-accent-700">
                  作品推荐
                </Link>
                页面开始探索。
              </p>
            </section>

            <section id="contact" className="scroll-mt-24">
              <h2 className="font-heading text-2xl font-extrabold text-foreground-950">联系方式</h2>
              <p className="mt-3 text-sm text-foreground-500">以下联系方式均为演示内容。</p>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                    <i className="ri-mail-line text-2xl" />
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-900">联系邮箱</h3>
                    <a
                      href="mailto:hello@91anime.demo"
                      className="text-sm text-accent-600 hover:text-accent-700"
                    >
                      hello@91anime.demo
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className="ri-qr-code-line text-2xl" />
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-900">微信公众号</h3>
                    <p className="text-sm text-foreground-500">搜索「91动漫」关注我们（演示）</p>
                  </div>
                </div>
              </div>
            </section>

            <section id="disclaimer" className="scroll-mt-24">
              <h2 className="font-heading text-2xl font-extrabold text-foreground-950">版权及免责声明</h2>
              <div className="mt-4 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100/60 p-5 text-sm leading-relaxed text-foreground-600">
                <p>
                  本站所有作品名称、简介、资讯内容与图片均为演示用途，与任何真实作品、机构或品牌无关，
                  不构成任何形式的介绍、推荐或评价。
                </p>
                <p>
                  本站不提供在线播放、下载等任何视听服务，相关内容仅用于展示页面功能与排版效果。
                  如需观看作品，请通过官方授权的正规渠道。
                </p>
                <p>© 2026 91动漫 · 本页面为演示站点，保留最终解释权。</p>
              </div>
            </section>
          </div>
        </div>
      </section>
    </div>
  );
}